import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { adminAPI, apiError } from '@/lib/adminApi';

const KIND_LABEL = { cluster: 'Cluster', association: 'Association' };

function KindPicker({ value, onChange, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm">
      <option value="association">Association</option>
      <option value="cluster">Cluster</option>
    </select>
  );
}

/**
 * Clusters and associations (PR 67). The support team creates them and
 * names each one's manager by phone number. A manager is an ordinary
 * farmer account; once assigned, offtakers message them about the
 * cluster's produce and they see its farmers' stock.
 */
export default function AdminClustersPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('association');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (query) => {
    const res = await adminAPI.clusters(query);
    setRows(res.data.clusters);
  }, []);

  useEffect(() => {
    const id = setTimeout(() => { void load(q.trim()).catch(() => {}); }, 250);
    return () => clearTimeout(id);
  }, [q, load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await adminAPI.createCluster({ kind, name: name.trim(), manager_phone: phone.trim() || null });
      setName('');
      setPhone('');
      await load(q.trim());
    } catch (err) {
      setError(apiError(err, 'Could not create it.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 max-w-3xl" data-testid="admin-clusters">
      <h1 className="font-heading text-xl font-bold text-farm-forest mb-1">Clusters &amp; associations</h1>
      <p className="text-sm text-farm-forest/70 mb-4">
        Farmers can sell through a cluster or association once it has a manager. The manager must sign up
        in the app as a farmer first; then enter their phone number here.
      </p>

      <form onSubmit={create} className="bg-white rounded-2xl p-4 shadow-sm mb-4 grid gap-2 sm:grid-cols-[auto_1fr_1fr_auto]">
        <KindPicker value={kind} onChange={setKind} testId="admin-cluster-kind" />
        <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2}
               maxLength={120} data-testid="admin-cluster-name" />
        <Input type="tel" placeholder="Manager's phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)}
               maxLength={20} data-testid="admin-cluster-phone" />
        <Button type="submit" className="bg-farm-primary" disabled={busy} data-testid="admin-cluster-create">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create</>}
        </Button>
        {error && <p className="text-sm text-red-600 sm:col-span-4" role="alert">{error}</p>}
      </form>

      <div className="relative mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input className="pl-9 bg-white" placeholder="Search by name, manager or phone" value={q}
               onChange={(e) => setQ(e.target.value)} data-testid="admin-cluster-search" />
      </div>

      <ul className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100" data-testid="admin-cluster-list">
        {rows.length === 0 && <li className="p-4 text-sm text-farm-forest/60">None yet.</li>}
        {rows.map((row) => (
          editing === row.id
            ? <EditRow key={row.id} row={row} onDone={async () => { setEditing(null); await load(q.trim()); }}
                       onCancel={() => setEditing(null)} />
            : (
              <li key={row.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid="admin-cluster-row">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-farm-forest break-words">{row.name}</p>
                  <p className="text-xs text-farm-forest/60">
                    {KIND_LABEL[row.kind]} · {row.manager
                      ? <>Manager: {row.manager.name} ({row.manager.phone})</>
                      : <span className="text-amber-700 font-semibold">No manager yet: farmers can&apos;t sell through it</span>}
                  </p>
                  <p className="text-xs text-farm-forest/60">
                    {row.farmers} farmer{row.farmers === 1 ? '' : 's'} selling · {row.listings} listing{row.listings === 1 ? '' : 's'}
                  </p>
                </div>
                <Button size="sm" variant="outline" className="ml-auto" onClick={() => setEditing(row.id)}
                        data-testid="admin-cluster-edit">Edit</Button>
              </li>
            )
        ))}
      </ul>
    </div>
  );
}

function EditRow({ row, onDone, onCancel }) {
  const [kind, setKind] = useState(row.kind);
  const [name, setName] = useState(row.name);
  const [phone, setPhone] = useState(row.manager?.phone || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async (fields) => {
    setBusy(true);
    setError('');
    try {
      await adminAPI.updateCluster(row.id, fields);
      await onDone();
    } catch (err) {
      setError(apiError(err, 'Could not save.'));
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const fields = {};
    if (kind !== row.kind) fields.kind = kind;
    if (name.trim() !== row.name) fields.name = name.trim();
    if (phone.trim() !== (row.manager?.phone || '')) fields.manager_phone = phone.trim();
    if (Object.keys(fields).length === 0) return onCancel();
    return save(fields);
  };

  return (
    <li className="p-3 bg-farm-cream/50" data-testid="admin-cluster-editing">
      <form onSubmit={submit} className="grid gap-2 sm:grid-cols-[auto_1fr_1fr]">
        <KindPicker value={kind} onChange={setKind} />
        <Input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={120} />
        <Input type="tel" placeholder="Manager's phone" value={phone} onChange={(e) => setPhone(e.target.value)}
               maxLength={20} data-testid="admin-cluster-edit-phone" />
        <div className="flex gap-2 flex-wrap sm:col-span-3">
          <Button type="submit" size="sm" className="bg-farm-primary" disabled={busy} data-testid="admin-cluster-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
          {row.manager && (
            <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => save({ manager_phone: '' })}
                    data-testid="admin-cluster-remove-manager">Remove manager</Button>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
        {error && <p className="text-sm text-red-600 sm:col-span-3" role="alert">{error}</p>}
      </form>
    </li>
  );
}
